import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../services/apiClient';
import { motion, AnimatePresence } from 'motion/react';
import { Satellite, Plus, Trash2, Save, ExternalLink, AlertTriangle, RefreshCcw, CheckCircle2, Wifi, WifiOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

interface RemoteDevice {
  name: string;
  url: string;
}

interface DeviceStatus {
  status: 'online' | 'offline' | 'reconnecting' | 'unknown';
  lastSeen?: string;
}

export function RemoteMCPSettings({ t }: { t?: any }) {
  const [devices, setDevices] = useState<RemoteDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [deviceStatuses, setDeviceStatuses] = useState<Record<string, DeviceStatus>>({});
  const [testingDevice, setTestingDevice] = useState<string | null>(null);
  const snapshotRef = useRef<RemoteDevice[]>([]);
  const undoDeleteRef = useRef<{ device: RemoteDevice; idx: number } | null>(null);

  // ── 表单验证 ──
  const validate = useCallback((list: RemoteDevice[]) => {
    const errors: Record<number, { name?: string; url?: string }> = {};
    const names = new Set<string>();
    let valid = true;
    for (let i = 0; i < list.length; i++) {
      const d = list[i];
      const rowErrors: { name?: string; url?: string } = {};
      if (!d.name.trim()) { rowErrors.name = '设备名称不能为空'; valid = false; }
      else if (names.has(d.name.trim())) { rowErrors.name = `名称「${d.name.trim()}」重复`; valid = false; }
      else { names.add(d.name.trim()); }
      if (!d.url.trim()) { rowErrors.url = '设备地址不能为空'; valid = false; }
      else if (!/^wss?:\/\/.+/.test(d.url.trim())) { rowErrors.url = '必须以 ws:// 或 wss:// 开头'; valid = false; }
      if (Object.keys(rowErrors).length > 0) errors[i] = rowErrors;
    }
    return { valid, errors };
  }, []);

  const [validationErrors, setValidationErrors] = useState<Record<number, { name?: string; url?: string }>>({});

  // ── 数据加载 ──
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/remote-devices');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const map = data.devices || {};
      const list = Object.entries(map).map(([name, url]) => ({
        name,
        url: typeof url === 'string' ? url : (url as any)?.url || '',
      }));
      setDevices(list);
      setValidationErrors({});
    } catch (err: any) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  // ── 设备状态轮询 ──
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await apiFetch('/api/remote-devices/status');
        if (res.ok) setDeviceStatuses(await res.json());
      } catch { /* ignore poll failures */ }
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, []);

  // ── 未保存提醒 ──
  useEffect(() => {
    if (!editing) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editing]);

  // ── 保存 ──
  const saveDevices = async (list: RemoteDevice[]) => {
    const { valid, errors } = validate(list);
    setValidationErrors(errors);
    if (!valid) return;

    const map: Record<string, string> = {};
    for (const d of list) {
      if (d.name.trim() && d.url.trim()) map[d.name.trim()] = d.url.trim();
    }
    try {
      const res = await apiFetch('/api/remote-devices', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ devices: map }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDevices(list.filter(d => d.name.trim() && d.url.trim()));
      setEditing(false);
      setValidationErrors({});
      toast.success(t?.remoteDeviceEndpointsSaved || '远程设备端点已保存');
    } catch (err: any) {
      toast.error(`${t?.failedToSaveEndpoints || '保存失败'}: ${err.message}`);
    }
  };

  // ── 编辑操作 ──
  const startEdit = () => {
    snapshotRef.current = devices.map(d => ({ ...d }));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDevices(snapshotRef.current);
    setEditing(false);
    setValidationErrors({});
    setDeletingIndex(null);
  };

  const addRow = () => {
    // 🔧 v3.12.45: 每次 addRow 都更新快照，避免多次添加后取消丢失中间编辑
    snapshotRef.current = devices.map(d => ({ ...d }));
    setEditing(true);
    setDevices(prev => [...prev, { name: '', url: 'wss://' }]);
  };

  const removeRow = (idx: number) => {
    setDeletingIndex(null);
    snapshotRef.current = devices.map(d => ({ ...d }));
    setEditing(true);
    const removed = devices[idx];
    // 🔧 v3.12.45: 用 ref 记录删除的设备引用，避免闭包过期导致撤销位置错误
    undoDeleteRef.current = { device: removed, idx };
    setDevices(prev => prev.filter((_, i) => i !== idx));
    toast(`${t?.deviceRemoved || '已删除设备'}「${removed.name || '未命名'}」`, {
      action: {
        label: t?.undo || '撤销',
        onClick: () => {
          const undo = undoDeleteRef.current;
          if (undo) {
            setDevices(prev => {
              const next = [...prev];
              // 在原始位置或末尾插入
              next.splice(Math.min(undo.idx, next.length), 0, undo.device);
              return next;
            });
            undoDeleteRef.current = null;
          }
        },
      },
      duration: 5000,
    });
  };

  const updateRow = (idx: number, field: 'name' | 'url', value: string) => {
    setEditing(true);
    setDevices(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
    // 实时清除该行校验错误
    setValidationErrors(prev => {
      const next = { ...prev };
      if (next[idx]?.[field]) {
        const row = { ...next[idx] };
        delete row[field];
        if (Object.keys(row).length === 0) delete next[idx];
        else next[idx] = row;
      }
      return next;
    });
  };

  // ── 测通按钮 ──
  const testConnection = async (device: RemoteDevice) => {
    setTestingDevice(device.name);
    try {
      const res = await apiFetch(`/api/remote-devices/${encodeURIComponent(device.name)}/test`, { method: 'POST' });
      const result = await res.json();
      if (result.success) {
        toast.success(`${t?.connectionTestSuccess || '连接成功'} · ${result.latencyMs}ms`);
      } else {
        toast.error(`${t?.connectionTestFailed || '连接失败'}: ${result.error || ''}`);
      }
    } catch (err: any) {
      toast.error(`${t?.connectionTestFailed || '连接失败'}: ${err.message}`);
    } finally {
      setTestingDevice(null);
    }
  };

  // ── 状态图标映射 ──
  const getStatusIcon = (name: string) => {
    const s = deviceStatuses[name];
    if (!s || s.status === 'unknown') return null;
    if (s.status === 'online') return <Wifi size={12} className="text-green-400" />;
    if (s.status === 'reconnecting') return <Wifi size={12} className="text-yellow-400 animate-pulse" />;
    return <WifiOff size={12} className="text-red-400/60" />;
  };

  const getStatusDot = (name: string) => {
    const s = deviceStatuses[name];
    if (!s || s.status === 'unknown') return 'bg-gray-500';
    if (s.status === 'online') return 'bg-green-400 animate-pulse shadow-[0_0_6px_rgba(74,222,128,0.5)]';
    if (s.status === 'reconnecting') return 'bg-yellow-400 animate-pulse';
    return 'bg-red-400/60';
  };

  // ── 设备名自动图标 ──
  const getDeviceIcon = (name: string) => {
    const lower = name.toLowerCase();
    if (lower.includes('xiaozhi') || lower.includes('speaker') || lower.includes('音箱')) return '🔊';
    if (lower.includes('glass') || lower.includes('ar') || lower.includes('眼镜')) return '👓';
    if (lower.includes('phone') || lower.includes('mobile') || lower.includes('手机')) return '📱';
    if (lower.includes('desktop') || lower.includes('pc') || lower.includes('电脑')) return '🖥️';
    if (lower.includes('car') || lower.includes('vehicle')) return '🚗';
    return '🌐';
  };

  // ── 加载态 — Skeleton ──
  if (loading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center gap-3">
          <Satellite className="text-celestial-saturn" />
          <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{t?.remoteMCPDevices || '远程 MCP 设备'}</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="p-5 bg-white/[0.06] rounded-[2rem] border border-white/[0.06] animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/[0.08]" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-28 bg-white/[0.08] rounded-lg" />
                  <div className="h-3 w-44 bg-white/[0.04] rounded-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── 错误态 ──
  if (error) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center gap-3">
          <Satellite className="text-celestial-saturn" />
          <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{t?.remoteMCPDevices || '远程 MCP 设备'}</h3>
        </div>
        <div className="p-8 bg-red-500/5 rounded-[2rem] border border-red-500/15 text-center">
          <AlertTriangle size={28} className="mx-auto text-red-400 mb-3" />
          <p className="text-sm text-red-400 font-bold mb-1">{t?.remoteLoadFailed || '加载失败'}</p>
          <p className="text-xs text-white/40">{error}</p>
          <Button onClick={fetchDevices} className="mt-4 bg-white/[0.08] hover:bg-white/[0.12] border border-white/10 text-xs font-bold px-5 h-10 rounded-xl">
            <RefreshCcw size={14} className="mr-2" /> {t?.retry || '重试'}
          </Button>
        </div>
      </div>
    );
  }

  // ── 主体 ──
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* ── 头部 ── */}
      <div className="flex items-center gap-3">
        <Satellite className="text-celestial-saturn" />
        <h3 className="text-xl font-bold uppercase tracking-tighter text-white/90">{t?.remoteMCPDevices || '远程 MCP 设备'}</h3>
      </div>

      <p className="text-sm text-white/50 max-w-xl leading-relaxed">
        {t?.remoteMCPDesc || '远程设备通过 MCP over WebSocket 连接小枫，调用 AI 工具能力。配置修改后需重启服务生效。'}
      </p>

      {/* ── 设备列表 ── */}
      {devices.length === 0 ? (
        <div className="p-8 bg-white/[0.06] rounded-[2rem] border border-white/[0.06]">
          {/* 三栏引导空状态 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-5 bg-white/[0.04] rounded-2xl border border-white/[0.04] text-center">
              <span className="text-2xl block mb-2">📖</span>
              <p className="text-xs text-white/60 font-bold mb-1">了解远程 MCP</p>
              <p className="text-[11px] text-white/30 leading-relaxed">设备通过 WebSocket 接入小枫，调用对话、记忆、工具等 AI 能力</p>
            </div>
            <div className="p-5 bg-celestial-saturn/[0.06] rounded-2xl border border-celestial-saturn/[0.15] text-center">
              <span className="text-2xl block mb-2">🔌</span>
              <p className="text-xs text-white/60 font-bold mb-1">添加第一台设备</p>
              <Button onClick={addRow} className="mt-3 bg-celestial-saturn text-black rounded-full px-5 py-2 text-xs font-bold hover:scale-105 transition-transform">
                <Plus size={14} className="mr-1" /> 快速添加
              </Button>
            </div>
            <div className="p-5 bg-white/[0.04] rounded-2xl border border-white/[0.04] text-center">
              <span className="text-2xl block mb-2">🔗</span>
              <p className="text-xs text-white/60 font-bold mb-1">连接格式</p>
              <p className="text-[10px] text-white/30 font-mono break-all leading-relaxed">wss://设备IP/mcp/?token=xxx</p>
            </div>
          </div>
          {/* 添加按钮兜底 */}
          <div className="text-center">
            <Button onClick={addRow} className="bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] text-xs font-black uppercase tracking-widest px-5 h-10 rounded-xl">
              <Plus size={14} className="mr-2" /> {t?.addDevice || '添加设备'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {devices.map((device, i) => (
            <motion.div
              key={device.name || device.url || i}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.05 }}
              className={`p-5 rounded-[2rem] border transition-all duration-300 group ${
                validationErrors[i]
                  ? 'bg-red-500/[0.04] border-red-500/20'
                  : editing
                  ? 'bg-white/[0.06] border-celestial-saturn/20'
                  : 'bg-white/[0.06] border-white/[0.06] hover:border-white/[0.12] hover:-translate-y-0.5'
              }`}
            >
              {/* 卡片头部：图标 + 名称 + 状态 + 操作 */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* 设备图标 + 在线绿点 */}
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-white/[0.08] flex items-center justify-center text-lg">
                      {getDeviceIcon(device.name)}
                    </div>
                    {device.name && (
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-black ${getStatusDot(device.name)}`} />
                    )}
                  </div>

                  {/* 名称 + URL */}
                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <div className="space-y-2">
                        <Input
                          value={device.name}
                          onChange={e => updateRow(i, 'name', e.target.value)}
                          placeholder={t?.deviceNamePlaceholder || '设备名称（例如：xiaozhi）'}
                          className={`bg-white/[0.06] border rounded-xl py-2 text-sm font-bold ${
                            validationErrors[i]?.name ? 'border-red-500/50' : 'border-white/[0.08]'
                          }`}
                        />
                        {validationErrors[i]?.name && (
                          <p className="text-[10px] text-red-400 font-bold pl-1">{validationErrors[i].name}</p>
                        )}
                        <Input
                          value={device.url}
                          onChange={e => updateRow(i, 'url', e.target.value)}
                          placeholder={t?.deviceURLPlaceholder || 'wss://设备地址/mcp/?token=...'}
                          className={`bg-white/[0.06] border rounded-xl py-2 text-xs font-mono ${
                            validationErrors[i]?.url ? 'border-red-500/50' : 'border-white/[0.08]'
                          }`}
                        />
                        {validationErrors[i]?.url && (
                          <p className="text-[10px] text-red-400 font-bold pl-1">{validationErrors[i].url}</p>
                        )}
                      </div>
                    ) : (
                      <>
                        <h4 className="text-sm font-black text-white/90 truncate">{device.name || t?.unnamedDevice || '未命名设备'}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-[11px] text-white/45 font-mono truncate max-w-[280px]">{device.url}</p>
                          {device.url && (
                            <a href={device.url.replace(/^ws(s?):\/\//, 'http$1://')} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/50 flex-shrink-0">
                              <ExternalLink size={11} />
                            </a>
                          )}
                        </div>
                        {getStatusIcon(device.name) && (
                          <div className="flex items-center gap-1.5 mt-1">
                            {getStatusIcon(device.name)}
                            <span className="text-[10px] text-white/30">
                              {deviceStatuses[device.name]?.status === 'online' ? '在线' :
                               deviceStatuses[device.name]?.status === 'reconnecting' ? '重连中' : '离线'}
                              {deviceStatuses[device.name]?.lastSeen ? ` · ${deviceStatuses[device.name].lastSeen}` : ''}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* 操作按钮组 */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* 测通按钮 */}
                  <Button
                    onClick={() => testConnection(device)}
                    disabled={testingDevice === device.name || !/^wss?:\/\/.+/.test(device.url)}
                    className="bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.06] text-white/40 hover:text-white/60 text-[10px] font-bold uppercase tracking-wider px-2.5 h-8 rounded-xl"
                  >
                    {testingDevice === device.name ? (
                      <div className="w-3 h-3 border border-white/30 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CheckCircle2 size={13} />
                    )}
                  </Button>
                  {/* 删除按钮 */}
                  <Button
                    onClick={() => setDeletingIndex(i)}
                    className="bg-red-500/[0.08] hover:bg-red-500/[0.18] border border-red-500/[0.12] text-red-400/70 hover:text-red-400 text-[10px] font-bold uppercase tracking-wider px-2.5 h-8 rounded-xl"
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}

          {/* 底部操作栏 */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={addRow}
              className="bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.06] text-xs font-black uppercase tracking-widest px-4 h-10 rounded-xl"
            >
              <Plus size={14} className="mr-2" /> {t?.addDevice || '添加设备'}
            </Button>
            {editing && (
              <>
                <Button
                  onClick={() => saveDevices(devices)}
                  className="bg-celestial-saturn text-black font-bold text-xs px-6 h-10 rounded-xl hover:scale-105 transition-transform"
                >
                  <Save size={14} className="mr-2" /> {t?.saveChanges || '保存更改'}
                </Button>
                <Button
                  onClick={cancelEdit}
                  className="bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] text-white/40 hover:text-white/60 text-xs font-bold px-4 h-10 rounded-xl"
                >
                  {t?.cancel || '取消'}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── 工作原理 ── */}
      <div className="p-5 bg-white/[0.06] rounded-[2rem] border border-white/[0.06] space-y-3">
        <div className="flex items-center gap-2.5">
          <Satellite className="text-celestial-saturn" size={16} />
          <h4 className="text-sm font-bold uppercase tracking-tight text-white/80">{t?.howItWorks || '工作原理'}</h4>
        </div>
        <p className="text-xs text-white/45 leading-relaxed">
          {t?.remoteMCPHowItWorks || '远程设备作为 MCP 客户端通过 WebSocket 连接小枫的 MCP 服务端。小枫主动发起连接，设备发送 MCP initialize 请求，小枫作为服务端响应 — 暴露 xf_chat、xf_memory_search、xf_tool_execute 等工具供设备通过语音或其他输入方式调用。'}
        </p>
      </div>

      {/* ── 删除确认弹窗 ── */}
      <AnimatePresence>
        {deletingIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setDeletingIndex(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#14141a] border border-white/[0.10] rounded-3xl p-6 w-[360px] max-w-[90vw] shadow-2xl"
              role="alertdialog"
              aria-label={t?.confirmDeleteDevice || '确认删除设备'}
            >
              <div className="text-center mb-5">
                <div className="w-12 h-12 rounded-full bg-red-500/[0.12] border border-red-500/[0.2] flex items-center justify-center mx-auto mb-3">
                  <Trash2 size={22} className="text-red-400" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">{t?.deleteDeviceConfirm || '确定删除此设备？'}</h4>
                <p className="text-xs text-white/45">
                  {t?.deleteDeviceConfirmDesc?.replace('{name}', devices[deletingIndex]?.name || '未命名') || `设备「${devices[deletingIndex]?.name || '未命名'}」将被移除，此操作可撤销。`}
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setDeletingIndex(null)}
                  className="flex-1 bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] text-white/60 text-sm font-bold h-11 rounded-xl"
                >
                  {t?.cancel || '取消'}
                </Button>
                <Button
                  onClick={() => removeRow(deletingIndex)}
                  className="flex-1 bg-red-500/[0.15] hover:bg-red-500/[0.25] border border-red-500/[0.2] text-red-400 text-sm font-bold h-11 rounded-xl"
                >
                  {t?.delete || '删除'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
